// Portfolio comparability + a mocked insight engine (v3 §Chunk 6). Normalise the sites onto
// comparable unit metrics, find the outliers, and have the "insight engine" name what matters.

import type { WorkspaceSeed } from "./types";
import { isOpenProjectStatus } from "./status";

export type UnitMetric = {
  projectId: string;
  name: string;
  budgeted: number;
  spentPct: number; // 0..1
  evidencePct: number; // 0..1 of spend evidenced
  driftDays: number;
  outlier: boolean;
};

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}
function std(xs: number[], m: number): number {
  return xs.length ? Math.sqrt(mean(xs.map((x) => (x - m) ** 2))) : 0;
}

export function unitMetrics(seed: WorkspaceSeed): UnitMetric[] {
  const ps = seed.projects.filter((p) => isOpenProjectStatus(p.status));
  const rows = ps.map((p) => ({
    projectId: p.id,
    name: p.name,
    budgeted: p.budgeted,
    spentPct: p.budgeted ? p.spent / p.budgeted : 0,
    evidencePct: p.spent ? p.evidenced / p.spent : 1,
    driftDays: p.scheduleDriftDays ?? 0,
    outlier: false,
  }));
  // Flag outliers on pace, evidence coverage or drift (more than ~1 std from the mean).
  const paceM = mean(rows.map((r) => r.spentPct));
  const paceS = std(rows.map((r) => r.spentPct), paceM);
  const evM = mean(rows.map((r) => r.evidencePct));
  const evS = std(rows.map((r) => r.evidencePct), evM);
  const drM = mean(rows.map((r) => r.driftDays));
  const drS = std(rows.map((r) => r.driftDays), drM);
  for (const r of rows) {
    r.outlier =
      (paceS > 0 && r.spentPct > paceM + paceS) ||
      (evS > 0 && r.evidencePct < evM - evS) ||
      (drS > 0 && r.driftDays > drM + drS);
  }
  return rows.sort((a, b) => b.driftDays - a.driftDays);
}

export type Insight = {
  id: string;
  kind: "outlier" | "watch" | "positive";
  title: string;
  body: string;
};

const pct = (x: number) => `${Math.round(x * 100)}%`;

/** Mocked "insight engine": discerns the metrics that matter for this programme and narrates them. */
export function generateInsights(seed: WorkspaceSeed): Insight[] {
  const rows = unitMetrics(seed);
  if (rows.length === 0) return [];
  const out: Insight[] = [];
  const medianDrift = [...rows].map((r) => r.driftDays).sort((a, b) => a - b)[Math.floor(rows.length / 2)] ?? 0;

  const worstDrift = rows[0];
  if (worstDrift.driftDays > 5) {
    const factor = medianDrift > 0 ? (worstDrift.driftDays / medianDrift).toFixed(1) : "—";
    out.push({
      id: "ins-drift",
      kind: "outlier",
      title: "Schedule is the metric that matters most this period",
      body: `${worstDrift.name} is the portfolio's schedule outlier at +${worstDrift.driftDays} days, around ${factor}× the median drift. Pace and evidence are broadly in line across the other sites, so schedule, not budget, is where oversight attention earns the most.`,
    });
  }

  const lowEvidence = [...rows].sort((a, b) => a.evidencePct - b.evidencePct)[0];
  if (lowEvidence && lowEvidence.evidencePct < 0.8) {
    out.push({
      id: "ins-evidence",
      kind: "watch",
      title: "Evidence coverage is uneven",
      body: `${lowEvidence.name} has the thinnest evidence coverage at ${pct(lowEvidence.evidencePct)} of spend, against a portfolio that is otherwise well evidenced. Closing this gap would lift the assurance on the next disbursement.`,
    });
  }

  const hotPace = [...rows].sort((a, b) => b.spentPct - a.spentPct)[0];
  if (hotPace) {
    out.push({
      id: "ins-pace",
      kind: hotPace.spentPct > 0.85 ? "outlier" : "positive",
      title: hotPace.spentPct > 0.85 ? "One site is approaching its budget ceiling" : "Budget pace is healthy",
      body:
        hotPace.spentPct > 0.85
          ? `${hotPace.name} has consumed ${pct(hotPace.spentPct)} of its budget, worth checking the remaining scope fits the envelope.`
          : `No site has consumed more than ${pct(hotPace.spentPct)} of its budget; spend pace is not the binding risk this period.`,
    });
  }

  return out;
}
